// Localized UI chrome strings for the catalog, decoupled from the @uag i18n.
// Edit here to change wording; the DE/EN switch (catalogLocaleAtom) picks the set.
// (Data-driven labels — facets, attributes, tabs — already carry their own de/en maps.)

import { useAtom } from 'jotai';

import { catalogLocaleAtom } from './state';

export const catalogLabels = {
    de: {
        title: 'Product Information Center',
        allProducts: 'Alle Produkte',
        // Chip group of a landing-page multi-select scope (state.scopeNodeIdsAtom) in the active-
        // filter row under the toolbar: "Eingegrenzt auf: A ×".
        scopedTo: 'Eingegrenzt auf',
        categories: 'Kategorien',
        filters: 'Filter',
        documentFilters: 'Dokumentfilter',
        clearFilters: 'Zurücksetzen',
        // Chip row of the active filters under the toolbar (components/ActiveFilterChips.tsx):
        // accessible name of the row, of each chip's ×, and the "remove all" link beside them.
        activeFilters: 'Aktive Filter',
        removeFilter: 'Filter entfernen',
        clearAllFilters: 'Alle löschen',
        // Merkmalsfilter of a system's Artikelliste (SystemDetailContent): its own drawer heading,
        // the hint under it, the caption of its chip row and the drawer's "back to the list" button.
        articleFilterTitle: 'Artikel filtern',
        systemFilterHint: 'Gilt nur für die {count} Artikel dieses Systems. Der übrige Katalog bleibt unverändert.',
        systemFilterChipsLabel: 'Filter in {name}:',
        systemDocumentFilterHint: 'Gilt nur für die Dokumente dieses Systems. Der übrige Katalog bleibt unverändert.',
        showArticles: '{count} Artikel anzeigen',
        // Chip of a free-text column filter (table header funnel), e.g. "Name: enthält „Framax“".
        columnFilterContains: 'enthält „{value}“',
        // In place of the hit count while the typed term is still too short to search with.
        searchMinChars: 'Mindestens {count} Zeichen eingeben',
        // A system's chip row: the catalog filters that also narrow its Artikelliste, and what
        // they hide there.
        catalogFiltersLabel: 'Aus dem Katalog:',
        hiddenByCatalogFilters: '{hidden} von {total} Artikeln durch Katalogfilter ausgeblendet',
        // One chip for an untouched preset ("Weltweites Sortiment") instead of its single filters.
        assortment: 'Sortiment',
        // "Link teilen" / "Suchprofile" in the toolbar's tab row (components/SearchShareActions.tsx).
        shareLink: 'Link teilen',
        shareLinkHint: 'Link zu genau dieser Suche kopieren (Suchbegriff, Kategorie, Filter, Ansicht)',
        shareLinkCopied: 'Link in die Zwischenablage kopiert',
        shareLinkManualCopy: 'Die Zwischenablage ist hier nicht verfügbar – bitte den Link kopieren:',
        searchProfiles: 'Suchprofile',
        saveSearchProfile: 'Aktuelle Suche speichern…',
        savedSearchProfiles: 'Gespeicherte Suchen',
        noSearchProfiles: 'Noch keine Suche gespeichert.',
        searchProfilesLocalHint: 'Nur in diesem Browser gespeichert. Zum Weitergeben „Link teilen“ verwenden.',
        searchProfileName: 'Name',
        searchProfileSaved: 'Suche „{name}“ gespeichert',
        deleteSearchProfile: 'Löschen',
        save: 'Speichern',
        cancel: 'Abbrechen',
        // Empty state of the searchable filter selects (Sprache/Dokumenttyp, Gruppe/Familie/System).
        noFilterMatch: 'Kein Treffer',
        facetFrom: 'von',
        facetTo: 'bis',
        facetExactHint: 'Für einen exakten Wert in beide Felder denselben Wert eintragen.',
        // Facet groups in the filter drawer (see components/FacetGroup.tsx): whole-group toggle
        // (aria-label of the heading button) and the "show more" cap of long value lists.
        facetExpand: 'Aufklappen',
        facetCollapse: 'Zuklappen',
        facetShowMore: '{count} weitere anzeigen',
        facetShowLess: 'Weniger anzeigen',
        // Search field above a value list with more than VALUE_LIST_SEARCH_THRESHOLD entries.
        facetSearchPlaceholder: 'Werte suchen…',
        yes: 'Ja',
        searchPlaceholder: 'Produkte und Teile suchen…',
        viewMode: 'Ansicht',
        viewGrid: 'Kacheln',
        viewList: 'Liste',
        columns: 'Spalten',
        filter: 'Filter',
        filterPlaceholder: 'enthält…',
        // Placeholder for a column filter that offers a value dropdown instead of free text
        // (Kategorie - see ProductGrid's FilterCell): 'enthält…' would misdescribe it.
        filterSelectPlaceholder: 'Alle',
        perPage: 'Pro Seite',
        results: 'Treffer',
        noResults: 'Keine passenden Artikel.',
        // Heading of the tree's own hits below the article hits (components/TreeSearchHits.tsx).
        treeHits: 'Treffer im Katalogbaum',
        // Placeholder for an article with no name in the active language (ProductGrid).
        noName: 'Ohne Namen',
        didYouMean: 'Meinten Sie',
        searchResults: 'Suchergebnisse',
        loadError: 'Der Katalog konnte nicht geladen werden.',
        product: 'Produkt',
        part: 'Teil',
        family: 'Familie',
        backToCatalog: 'Zurück zum Katalog',
        articleNumber: 'Artikelnummer',
        manufacturer: 'Hersteller',
        category: 'Kategorie',
        // Heading of the same column when only the last tree level is shown (browsing the category
        // tree instead of searching - see ProductGrid's categoryDisplay).
        system: 'System',
        name: 'Name',
        noMedia: 'Keine Medien vorhanden.',
        // "Dokumente"-Reiter der Artikelseite: eigene Dokumente des Artikels gegen die des
        // übergeordneten Systems. Die eigenen sind die Antwort auf die Frage, die des Systems
        // eine Ergänzung - deshalb getrennt und nicht in einer Liste vermischt.
        documentsOwn: 'Dieser Artikel',
        documentsFromSystem: 'Vom System',
        systemDocumentsAll: 'Alle',
        systemDocumentsDirect: 'Am System',
        systemDocumentsFromArticles: 'Aus Artikeln',
        systemDocumentsFromArticlesHint: 'Dokumente, die an Artikeln dieses Systems hängen, nicht am System selbst',
        noOwnMedia: 'Für diesen Artikel sind keine eigenen Dokumente hinterlegt.',
        systemDocumentsHint: 'Ergänzend: Dokumente des übergeordneten Systems',
        noData: 'Keine Daten vorhanden.',
        open: 'Öffnen',
        documentTitle: 'Titel',
        documentRelease: 'Freigabe',
        documentEditionDate: 'Ausgabedatum',
        documentEdition: 'Edition',
        documentCategory: 'Dokumenttyp',
        documentLanguage: 'Sprache',
        allLanguages: 'Alle Sprachen',
        allDocumentCategories: 'Alle Dokumenttypen',
        noFilteredMedia: 'Keine Dokumente für diese Auswahl.',
        backToArticle: 'Zurück zum Artikel',
        pdfContents: 'Inhalt',
        pdfPages: 'Seiten',
        pdfNavigation: 'Suche & Inhalt',
        pdfOf: 'von',
        pdfGoToPage: 'Zur Seite',
        pdfCloseSearch: 'Suche beenden',
        pdfPrevPage: 'Vorherige Seite',
        pdfNextPage: 'Nächste Seite',
        pdfNoOutline: 'Kein Inhaltsverzeichnis vorhanden.',
        pdfPage: 'Seite',
        pdfSearch: 'Im Dokument suchen…',
        pdfError: 'Das Dokument konnte nicht geladen werden.',
        pdfDocument: 'Dokument',
        pdfFallbackNotice:
            'Anzeige über den PDF-Betrachter des Browsers – Suche, Gliederung und Seitenvorschau stehen hier nicht zur Verfügung.',
        enlargeImage: 'Bild vergrößern',
        searchModeProducts: 'Produkte',
        searchModeDocuments: 'Dokumente',
        documentIndexTitle: 'Dokumenten-Index',
        documentIndexNoResults: 'Keine passenden Dokumente.',
        documentUsage: 'Verwendung',
        documentUsageInSystem: 'Verwendung im System',
        documentUsageSystemTab: 'Verwendung System',
        documentUsageCatalogTab: 'Verwendung Katalog',
        documentNoSystemUsage: 'In diesem System wird das Dokument keinem Artikel direkt zugeordnet.',
        documentBasics: 'Grunddaten',
        documentFile: 'Dokument',
        documentNoUsage: 'Dieses Dokument ist keinem Artikel und keinem System zugeordnet.',
        usageAssignment: 'Zuordnung',
        usageDetails: 'Details',
        usageKindArticle: 'Artikel',
        usageKindSystem: 'System',
        documentUsageArticles: 'Artikel',
        documentUsageNodes: 'Systeme',
        usedInSystems: 'Eingesetzt in folgenden Systemen',
        // Eigener Tab der Artikelansicht (hinter "Daten"), siehe ArticleDetailPage: ein Artikel
        // kann in mehreren Systemen verbaut sein.
        articleUsageTab: 'Verwendung',
        resizeTree: 'Breite der Navigation ändern – ziehen, Doppelklick setzt zurück',
        noArticleUsage: 'Dieser Artikel ist keinem System zugeordnet.',
        // Eigene Tabs der Artikelansicht, nur wenn der Artikel solche Beziehungen hat (STIBO
        // REF_spare_part_article / REF_substitute_article, siehe ArticleDetail.relations).
        sparePartsTab: 'Ersatzteile',
        substitutesTab: 'Austauschartikel',
        relationSparePart: 'Ersatzteil',
        relationSparePartOf: 'Ersatzteil für',
        relationSubstitute: 'Austauschartikel',
        relationSubstituteFor: 'Austauschartikel für',
        documentSearchPlaceholder: 'Dokumente durchsuchen…',
        back: 'Zurück',
        close: 'Schließen',
        maximize: 'Maximieren',
        restore: 'Verkleinern',
        download: 'Download',
        openInNewTab: 'In neuem Tab öffnen',
        openShort: 'Öffnen',
        scopeLocal: 'Lokal',
        welcomeGreeting: 'Herzlich willkommen',
        landingNews: 'Neuigkeiten',
        landingNoNews: 'Aktuell keine Neuigkeiten.',
        wishlist: 'Merkliste',
        addToWishlist: 'Zur Merkliste hinzufügen',
        removeFromWishlist: 'Von der Merkliste entfernen',
        clearWishlist: 'Merkliste leeren',
        confirmClearWishlist: 'Wirklich die gesamte Merkliste löschen?',
        wishlistEmpty: 'Die Merkliste ist leer.',
        wishlistProducts: 'Produkte',
        wishlistDocuments: 'Dokumente',
        systemInfoTab: 'Systeminformationen',
        systemArticlesTab: 'Artikelliste',
        systemDocumentsTab: 'Dokumente',
        // Short forms shown below `sm` only (see SystemDetailContent's Tabs.List): the three full
        // labels together are far wider than a phone screen, which pushed the tab row into an
        // unreadable squeeze.
        systemInfoTabShort: 'Infos',
        systemArticlesTabShort: 'Artikel',
        objectTypeSystem: 'System',
        noSystemInfo: 'Keine Systeminformationen vorhanden.',
        noSystemArticles: 'Keine Artikel diesem System zugeordnet.',
        noSystemDocuments: 'Keine Dokumente diesem System zugeordnet.',
        compareHint: 'Bis zu 3 Objekte für den Vergleich auswählen.',
        compareButton: 'Vergleichen',
        compareTitle: 'Produktvergleich',
        compareAttribute: 'Merkmal',
        searchMaskHeading: 'Wonach suchen Sie?',
        searchMaskProductData: 'Produkte',
        searchMaskDataKind: 'Datenart',
        searchMaskTerm: 'Suchbegriff',
        searchMaskPlaceholder: 'Suche…',
        searchMaskGroup: 'Gruppe',
        searchMaskFamily: 'Familie',
        searchMaskSystem: 'System',
        searchMaskChoose: 'Auswählen…',
        searchMaskMoreFilters: 'Mehr Filteroptionen',
        // The assortment checkbox of the landing mask (SearchMask): stands for the
        // "global-program" preset and locks Landesgesellschaft/Lifecycle while ticked.
        searchMaskGlobalAssortment: 'Weltweites Sortiment',
        // Instant-answer dropdown under the search field: section heading of the article rows
        // (with the total) and the "see everything" row below the capped list.
        searchMaskArticleHits: 'Artikel',
        searchMaskShowAllHits: 'Alle {count} Treffer anzeigen',
        searchMaskSubmit: 'Ergebnisse anzeigen',
        searchMaskLoading: 'Daten werden geladen',
        loadingStepTree: 'Navigation',
        loadingStepIndex: 'Artikelindex',
        loadingStepConfig: 'Konfiguration',
        loadingStepDocuments: 'Dokumente',
        loadingStepLabels: 'Beschriftungen',
        searchMaskStuckHint: 'Dauert das ungewöhnlich lange? Einmal irgendwo auf die Seite klicken hilft manchmal.',
    },
    en: {
        title: 'Product Information Center',
        allProducts: 'All products',
        scopedTo: 'Narrowed to',
        categories: 'Categories',
        filters: 'Filters',
        documentFilters: 'Document filters',
        clearFilters: 'Clear',
        activeFilters: 'Active filters',
        removeFilter: 'Remove filter',
        clearAllFilters: 'Clear all',
        articleFilterTitle: 'Filter articles',
        systemFilterHint: 'Applies only to the {count} articles of this system. The rest of the catalogue stays as it is.',
        systemFilterChipsLabel: 'Filters in {name}:',
        systemDocumentFilterHint: 'Applies only to the documents of this system. The rest of the catalogue stays as it is.',
        showArticles: 'Show {count} articles',
        columnFilterContains: 'contains "{value}"',
        searchMinChars: 'Enter at least {count} characters',
        catalogFiltersLabel: 'From the catalogue:',
        hiddenByCatalogFilters: '{hidden} of {total} articles hidden by catalogue filters',
        assortment: 'Assortment',
        shareLink: 'Share link',
        shareLinkHint: 'Copy a link to exactly this search (term, category, filters, view)',
        shareLinkCopied: 'Link copied to the clipboard',
        shareLinkManualCopy: 'The clipboard is not available here - please copy the link:',
        searchProfiles: 'Saved searches',
        saveSearchProfile: 'Save current search…',
        savedSearchProfiles: 'Saved searches',
        noSearchProfiles: 'No saved searches yet.',
        searchProfilesLocalHint: 'Stored in this browser only. Use "Share link" to pass a search on.',
        searchProfileName: 'Name',
        searchProfileSaved: 'Search "{name}" saved',
        deleteSearchProfile: 'Delete',
        save: 'Save',
        cancel: 'Cancel',
        noFilterMatch: 'No match',
        facetFrom: 'from',
        facetTo: 'to',
        facetExactHint: 'For an exact value, enter the same number in both fields.',
        facetExpand: 'Expand',
        facetCollapse: 'Collapse',
        facetShowMore: 'Show {count} more',
        facetShowLess: 'Show less',
        facetSearchPlaceholder: 'Search values…',
        yes: 'Yes',
        searchPlaceholder: 'Search products and parts…',
        viewMode: 'View',
        viewGrid: 'Tiles',
        viewList: 'List',
        columns: 'Columns',
        filter: 'Filter',
        filterPlaceholder: 'contains…',
        filterSelectPlaceholder: 'All',
        perPage: 'Per page',
        results: 'results',
        noResults: 'No matching articles.',
        treeHits: 'Matches in the catalog tree',
        noName: 'No name',
        didYouMean: 'Did you mean',
        searchResults: 'Search results',
        loadError: 'The catalog could not be loaded.',
        product: 'Product',
        part: 'Part',
        family: 'Family',
        backToCatalog: 'Back to catalog',
        articleNumber: 'Article number',
        manufacturer: 'Manufacturer',
        category: 'Category',
        system: 'System',
        name: 'Name',
        noMedia: 'No media available.',
        documentsOwn: 'This article',
        documentsFromSystem: 'From the system',
        systemDocumentsAll: 'All',
        systemDocumentsDirect: 'On the system',
        systemDocumentsFromArticles: 'From articles',
        systemDocumentsFromArticlesHint: 'Documents assigned to articles of this system, not to the system itself',
        noOwnMedia: 'No documents are assigned to this article itself.',
        systemDocumentsHint: 'Additional: documents of the parent system',
        noData: 'No data available.',
        open: 'Open',
        documentTitle: 'Title',
        documentRelease: 'Release',
        documentEditionDate: 'Edition date',
        documentEdition: 'Edition',
        documentCategory: 'Document type',
        documentLanguage: 'Language',
        allLanguages: 'All languages',
        allDocumentCategories: 'All document types',
        noFilteredMedia: 'No documents match this selection.',
        backToArticle: 'Back to article',
        pdfContents: 'Contents',
        pdfPages: 'Pages',
        pdfNavigation: 'Search & contents',
        pdfOf: 'of',
        pdfGoToPage: 'Go to page',
        pdfCloseSearch: 'End search',
        pdfPrevPage: 'Previous page',
        pdfNextPage: 'Next page',
        pdfNoOutline: 'No table of contents available.',
        pdfPage: 'Page',
        pdfSearch: 'Search in document…',
        pdfError: 'The document could not be loaded.',
        pdfDocument: 'Document',
        pdfFallbackNotice:
            'Shown in the browser\'s own PDF viewer - search, outline and page previews are not available here.',
        enlargeImage: 'Enlarge image',
        searchModeProducts: 'Products',
        searchModeDocuments: 'Documents',
        documentIndexTitle: 'Document index',
        documentIndexNoResults: 'No matching documents.',
        welcomeGreeting: 'Welcome',
        landingNews: 'News',
        landingNoNews: 'No news at the moment.',
        documentUsage: 'Usage',
        documentUsageInSystem: 'Usage in system',
        documentUsageSystemTab: 'Usage system',
        documentUsageCatalogTab: 'Usage catalog',
        documentNoSystemUsage: 'Within this system the document is not assigned to any article.',
        documentBasics: 'Basic data',
        documentFile: 'Document',
        documentNoUsage: 'This document is not assigned to any article or system.',
        usageAssignment: 'Assignment',
        usageDetails: 'Details',
        usageKindArticle: 'Article',
        usageKindSystem: 'System',
        documentUsageArticles: 'Articles',
        documentUsageNodes: 'Systems',
        usedInSystems: 'Used in the following systems',
        articleUsageTab: 'Usage',
        resizeTree: 'Resize the navigation - drag, double-click to reset',
        noArticleUsage: 'This article is not assigned to any system.',
        sparePartsTab: 'Spare parts',
        substitutesTab: 'Replacement articles',
        relationSparePart: 'Spare part',
        relationSparePartOf: 'Spare part for',
        relationSubstitute: 'Replacement article',
        relationSubstituteFor: 'Replacement article for',
        documentSearchPlaceholder: 'Search documents…',
        back: 'Back',
        close: 'Close',
        maximize: 'Maximize',
        restore: 'Restore',
        download: 'Download',
        openInNewTab: 'Open in new tab',
        openShort: 'Open',
        scopeLocal: 'Local',
        wishlist: 'Wishlist',
        addToWishlist: 'Add to wishlist',
        removeFromWishlist: 'Remove from wishlist',
        clearWishlist: 'Clear wishlist',
        confirmClearWishlist: 'Really clear the entire wishlist?',
        wishlistEmpty: 'The wishlist is empty.',
        wishlistProducts: 'Products',
        wishlistDocuments: 'Documents',
        systemInfoTab: 'System information',
        systemArticlesTab: 'Article list',
        systemDocumentsTab: 'Documents',
        systemInfoTabShort: 'Info',
        systemArticlesTabShort: 'Articles',
        objectTypeSystem: 'System',
        noSystemInfo: 'No system information available.',
        noSystemArticles: 'No articles assigned to this system.',
        noSystemDocuments: 'No documents assigned to this system.',
        compareHint: 'Select up to 3 objects to compare.',
        compareButton: 'Compare',
        compareTitle: 'Product comparison',
        compareAttribute: 'Attribute',
        searchMaskHeading: 'What are you looking for?',
        searchMaskProductData: 'Products',
        searchMaskDataKind: 'Data type',
        searchMaskTerm: 'Search term',
        searchMaskPlaceholder: 'Search…',
        searchMaskGroup: 'Group',
        searchMaskFamily: 'Family',
        searchMaskSystem: 'System',
        searchMaskChoose: 'Select…',
        searchMaskMoreFilters: 'More filter options',
        searchMaskGlobalAssortment: 'Global assortment',
        searchMaskArticleHits: 'Articles',
        searchMaskShowAllHits: 'Show all {count} hits',
        searchMaskSubmit: 'Show results',
        searchMaskLoading: 'Loading data',
        loadingStepTree: 'navigation',
        loadingStepIndex: 'article index',
        loadingStepConfig: 'configuration',
        loadingStepDocuments: 'documents',
        loadingStepLabels: 'labels',
        searchMaskStuckHint: 'Taking unusually long? Clicking anywhere on the page sometimes helps.',
    },
} as const;

// Widened to `string` per key: `de`'s `as const` values are narrow string-literal types (e.g.
// `allProducts: "Alle Produkte"`), which `en`'s own (differently-worded) literals are never
// assignable to - only the KEY SET should be shared between locales, not the exact German text.
export type CatalogLabels = { [K in keyof (typeof catalogLabels)['de']]: string };

export const getCatalogLabels = (locale: string): CatalogLabels =>
    locale === 'en' ? catalogLabels.en : catalogLabels.de;

/** Returns the active catalog locale and its label set. */
export const useCatalogLabels = (): { locale: string; L: CatalogLabels } => {
    const [locale] = useAtom(catalogLocaleAtom);
    return { locale, L: getCatalogLabels(locale) };
};
